/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import ClauseLibrary from './ClauseLibrary';
import ExcelTable from './ExcelTable';
import LoadingInsights from './LoadingInsights';
import SectionList from './SectionList';
import { getSections } from '../../axiosClient/draftApi';
import { WEBSOCKET_API } from '../../Configuration/global';
import { getLocalStorage } from '../../hooks/authentication';

interface IProps {
  activeTab: string;
  openBoot: VoidFunction;
  setBotFrom: any;
  draftId?: string;
}

const Sections = (props: IProps) => {
  const { activeTab, openBoot, setBotFrom, draftId } = props;
  const queryClient = useQueryClient();
  const { id } = useParams();

  const [expanded, setExpanded] = React.useState<string | false>(false);

  const [insightsStatus, setInsightsStatus] = useState<any>();

  const { data: sections, isLoading } = useQuery({
    queryKey: ['sections', id, draftId],
    queryFn: () => getSections(draftId || (id as string)),
    enabled: !!id || !!draftId,
  });

  useEffect(() => {
    const token = getLocalStorage('accessToken');
    let ws = new WebSocket(`${WEBSOCKET_API}ws/draft_status/?token=${token}`);

    ws.onmessage = (event) => {
      const insights_status = JSON.parse(event.data)['message'];
      insights_status?.map((data: any) => {
        if (data.id === id) {
          setInsightsStatus({
            status: data.status,
            progress: parseInt(data.percentage),
          });
          if (data.percentage === 100) {
            queryClient.invalidateQueries({
              queryKey: ['sections'],
            });
          }
        }
      });
    };

    ws.onclose = () => {
      ws = new WebSocket(`${WEBSOCKET_API}ws/draft_status/?token=${token}`);
      setInsightsStatus({
        status: 'Done',
        progress: 100,
      });
    };
  }, []);

  useEffect(() => {
    setExpanded(false);
  }, [activeTab]);

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  if (!insightsStatus && isLoading) {
    return (
      <Stack style={{ justifyContent: 'center', padding: '20px' }}>
        <CircularProgress />
      </Stack>
    );
  }
  if (activeTab === 'clause-library') {
    return (
      <div>
        {Object.entries(sections || {})?.map((section: any, index: number) => (
          <ClauseLibrary
            key={index}
            sections={section[1]}
            openBoot={openBoot}
            setBotFrom={setBotFrom}
          />
        ))}
      </div>
    );
  }
  if (
    (insightsStatus && insightsStatus.progress < 100) ||
    Object.keys(sections || {}).length === 0
  ) {
    return (
      <Stack
        sx={{
          height: '90%',
          overflowY: 'auto',
          borderRadius: '24px',
          padding: '8px',
          marginTop: '8px',
        }}
        gap="5px"
      >
        <LoadingInsights
          value={parseInt(insightsStatus?.progress || 0)}
          label={`Automatically computing ${activeTab} for the draft. They will be displayed shortly.`}
        />
      </Stack>
    );
  }
  return (
    <Stack style={{ padding: '10px' }}>
      {Object.entries(sections || {})?.map((section: any, key) => {
        return (
          <Accordion
            key={key}
            onChange={handleChange(section[0])}
            expanded={expanded === section[0]}
            className="according-class"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className="clause-heading"
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignContent="center"
                sx={{
                  width: '100%',
                }}
              >
                <Typography>{section[0]}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ margin: 0, padding: '0 0 8px' }}>
              <SectionList
                id={draftId || id}
                sections={section[1]}
                activeTab={activeTab}
                openBoot={openBoot}
                setBotFrom={setBotFrom}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
      <ExcelTable id={draftId || id} sections={sections} />
    </Stack>
  );
};

export default Sections;
